// Copyright (c) 2025-2026 Motswagae Holding (Pty) Ltd. All Rights Reserved.
// CIPC Registration: 2019/443098/07
// Author: Velly Sipho Phaloane
//
// This source code is the proprietary and confidential property of
// Motswagae Holding (Pty) Ltd, trading as 360Wallet.
// Unauthorized copying, modification, distribution, or use of this
// software, in whole or in part, is strictly prohibited.
//
// Protected under the Copyright Act 98 of 1978 (South Africa)
// and international copyright treaties.
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import {
  LayoutDashboard,
  Building2,
  KeyRound,
  Receipt,
  DollarSign,
  FileText,
  ScrollText,
  LogOut,
  Menu,
  X,
  Webhook,
  Settings,
  Network,
  FileSpreadsheet,
  Store,
  Play,
  Wallet,
  Landmark,
  Calculator,
  BookOpen,
  ShieldCheck,
  ArrowDownToLine,
  Bell
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { getNotifications, markNotificationRead, markAllNotificationsRead } from "../lib/api";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/admin/simulator", icon: Play, label: "ATM Withdrawal" },
  { to: "/admin/atm-deposit", icon: ArrowDownToLine, label: "ATM Deposit" },
  { to: "/admin/rails", icon: Network, label: "Clearing Rails" },
  { section: "Partners" },
  { to: "/admin/banks", icon: Building2, label: "Banks" },
  { to: "/admin/retail-stores", icon: Store, label: "Retail Stores" },
  { to: "/admin/atm-acquirers", icon: Network, label: "ATM Operators" },
  { section: "Operations" },
  { to: "/admin/authorizations", icon: KeyRound, label: "Authorizations" },
  { to: "/admin/transactions", icon: Receipt, label: "Withdrawal Entries" },
  { to: "/admin/deposits", icon: ArrowDownToLine, label: "Deposits Entries" },
  { section: "Finance" },
  { to: "/admin/deposit-limits", icon: ShieldCheck, label: "Deposit Limits" },
  { to: "/admin/fee-schedules", icon: Calculator, label: "Fee Schedules" },
  { to: "/admin/invoices", icon: FileSpreadsheet, label: "Invoices" },
  { to: "/admin/pricing", icon: DollarSign, label: "Pricing" },
  { to: "/admin/settlements", icon: FileText, label: "Settlements" },
  { to: "/admin/settlement-ledger", icon: BookOpen, label: "Settlement Ledger" },
  { to: "/admin/settlement-account", icon: Landmark, label: "Settlement Account" },
  { section: "System" },
  { to: "/admin/certificates", icon: ShieldCheck, label: "Certificates" },
  { to: "/admin/webhooks", icon: Webhook, label: "Webhooks" },
  { to: "/admin/audit-logs", icon: ScrollText, label: "Audit Logs" },
  { to: "/admin/settings", icon: Settings, label: "Settings" },
];

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await getNotifications(20);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkRead = async (id) => {
    await markNotificationRead(id);
    fetchNotifications();
  };

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    fetchNotifications();
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-60 bg-[#0a0f1a] border-r border-border
        transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-border">
            <div className="flex items-center gap-2">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/4f9c7ad5-2823-490f-b99e-1d24db3fbcdb/images/112c92c4a088172d03acdf972c6e2e3516082b3829d18e3efd9b6298f1e5a579.png" 
                alt="360Wallet" 
                className="w-8 h-8"
              />
              <span className="font-heading font-semibold text-lg bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">360Wallet</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navItems.map((item, index) => (
                item.section ? (
                  <div key={`section-${index}`} className="pt-4 pb-2 px-3">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {item.section}
                    </span>
                  </div>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/admin"}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `sidebar-item ${isActive ? 'active' : ''}`
                    }
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </NavLink>
                )
              ))}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-primary font-medium text-sm">
                  {user?.full_name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 glass-header sticky top-0 z-30 flex items-center justify-between px-4 lg:px-6">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          <div className="hidden lg:block">
            <h1 className="font-heading font-semibold text-lg">
              Interoperable Withdrawal Authorization Engine
            </h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative p-2 rounded-lg hover:bg-secondary transition-colors"
                data-testid="notification-bell"
              >
                <Bell className="h-5 w-5 text-muted-foreground" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1" data-testid="notif-badge">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>

              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden" data-testid="notification-panel">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                      <span className="text-sm font-semibold">Notifications</span>
                      {unreadCount > 0 && (
                        <button onClick={handleMarkAllRead} className="text-xs text-primary hover:underline" data-testid="mark-all-read-btn">
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications</div>
                      ) : (
                        notifications.slice(0, 10).map((n) => (
                          <div
                            key={n.id}
                            onClick={() => { if (!n.read) handleMarkRead(n.id); }}
                            className={`px-4 py-3 border-b border-border/50 cursor-pointer hover:bg-secondary/30 transition-colors ${!n.read ? "bg-primary/5" : ""}`}
                          >
                            <div className="flex items-start gap-2">
                              <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                                n.severity === "critical" ? "bg-red-500" :
                                n.severity === "warning" ? "bg-amber-500" : "bg-blue-500"
                              }`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${!n.read ? "font-medium" : "text-muted-foreground"}`}>{n.title}</p>
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                                <p className="text-[10px] text-muted-foreground/60 mt-1">
                                  {new Date(n.created_at).toLocaleString("en-ZA")}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {notifications.length > 0 && (
                      <div className="px-4 py-2 border-t border-border text-center">
                        <button onClick={() => { setNotifOpen(false); navigate("/admin/certificates"); }} className="text-xs text-primary hover:underline">
                          View all in Certificates
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="text-right">
              <p className="text-xs text-muted-foreground">Environment</p>
              <p className="text-sm font-medium text-accent">Production Ready</p>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
